/**
 * Singbac — Incorporation Pre chatflow (SG, Singbac 문서셋 · 3종).
 * Studio: title → 엔티티 선택(자동바인딩) → confirm + live document package
 *   (Draft Constitution · Form 45 Director Consent · Form 45B Secretary Consent · Notice to Controller).
 */
import type { ConversationMessage } from '@/features/AI-documents/types/docGen.types';
import type {
  ChatFlow,
  ChatFlowContext,
  ChatFlowEventResult,
} from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';

export const singbacIncorporationPreFlow: ChatFlow = {
  id: 'singbac-incorporation-pre',
  // ★ 공통코드(Config Code) CSB002 로 확정 매칭 — incorporationDocFlow(M32014, T-Link)/POST/Aura 가
  //   카테고리 'incorporation' 로 가로채지 못하게 taskCode 우선 매칭시킨다.
  taskCodeMatch: ['CSB002'],
  // 'singbac' 단독은 POST 카테고리도 잡을 수 있어 제외 — PRE 전용 문구만.
  categoryMatch: ['singbac incorporation pre', 'incorporation pre'],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `Welcome to **Singbac — Incorporation Pre**.\n\n` +
          `This preset prepares the pre-incorporation document package` +
          (docCount > 0 ? ` (${docCount} template(s) linked)` : '') +
          `: Draft Constitution, Form 45 (Consent to Act as Director), Form 45B ` +
          `(Consent to Act as Secretary), and Notice to Controller (s.386AG(2)(a)).\n\n` +
          `After you enter the task title, pick a **custom form** and one of its **submitted responses** ` +
          `on the right — the answers auto-fill the documents. You can then adjust any field, click **✨** ` +
          `on a name to look up a **Contact**, and **📍** for addresses.\n\n` +
          `First, please enter a **Task Title**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: { targetVarKey: '_taskTitle', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'select-form',
      aiMessage:
        'Select a **custom form** and one of its **submitted responses** — the answers auto-fill the documents, ' +
        'or **skip** to fill the documents manually.',
      widget: 'custom-form-picker',
      widgetConfig: { targetVarKey: '_customForm', targetEntityIndex: 0, allowSkip: true },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Review the documents on the right. Drag any value below onto a document field to fill leftover blanks, ' +
        'add copies per director / secretary / controller as needed, then click **Generate**.',
      // 선택한 커스텀폼 응답의 key:value 드래그 팔레트 (answerCd 는 이벤트에서 widgetData 로 주입)
      widget: 'custom-form-data',
      nextStep: null,
    },
    {
      id: 'officer-role',
      aiMessage: 'Please select a role for the person you want to look up.',
      widget: 'officer-role-buttons',
      awaitEvent: true,
    },
    {
      id: 'officer-list',
      aiMessage: 'Select a person from the list.',
      widget: 'officer-list',
      awaitEvent: true,
    },
    {
      id: 'officer-detail',
      aiMessage: 'Person details loaded. The form has been pre-filled.',
      widget: 'officer-field-picker',
      awaitEvent: true,
    },
    {
      id: 'address-input',
      aiMessage: 'Enter the address below.',
      widget: 'address-input',
      awaitEvent: true,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (
      (eventName === 'value-submit' || eventName === 'text-input-submit') &&
      context.currentStepId === 'input-title'
    ) {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { _taskTitle: String(title).trim(), taskTitle: String(title).trim() },
        // TASK 제목 직후 → 커스텀 폼 선택 위젯(챗봇).
        nextStepId: 'select-form',
      };
    }

    // 챗봇 커스텀 폼 위젯에서 폼 → 작성(제출) 선택 완료.
    //   customFormCd/customFormAnswerCd 를 formData 로 넘기면 패널(chatFormData)이 받아 자동채움한다.
    if (eventName === 'custom-form-answer-select') {
      const p = payload || {};
      const answerCd = String(p.customFormAnswerCd || '').trim();
      const formCd = String(p.customFormCd || '').trim();
      if (!answerCd) return {};
      return {
        messages: [
          {
            role: 'user',
            content: `Selected response: **${p._label || answerCd}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          customFormCd: formCd,
          customFormAnswerCd: answerCd,
          customFormLabel: String(p._label || ''),
        },
        nextStepId: 'confirm',
        // confirm 단계의 'custom-form-data' 위젯이 이 응답의 팔레트를 문서와 함께 표시.
        nextStepWidgetData: {
          customFormAnswerCd: answerCd,
          customFormCd: formCd,
          customFormLabel: String(p._label || ''),
        } as any,
      };
    }

    // 커스텀 폼 없이 진행 — 문서를 직접 작성 (다른 마이프리셋의 엔티티 Skip 과 같은 역할)
    if ((eventName === 'entity-skip' || eventName === 'skip') && context.currentStepId === 'select-form') {
      return {
        messages: [{ role: 'user', content: 'Skipped — fill manually.', timestamp: Date.now(), stepId: 1 }],
        formUpdates: { customFormCd: '', customFormAnswerCd: '', customFormLabel: '', entitySkipped: true },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const key = String(ctx.key || '');

      if (
        key === 'company_name' ||
        key === 'entity_name' ||
        ctx.lookupType === 'incorporation' ||
        (ctx.fieldGroup === 'singbac-incorporation-pre' &&
          !ctx.defaultRole &&
          ctx.lookupType !== 'ssic')
      ) {
        return {
          messages: [
            {
              role: 'user',
              content: 'Select a **custom form** response…',
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'select-form',
        };
      }
      return {
        messages: [
          {
            role: 'user',
            content: `Looking up person for **${key || 'field'}**…`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          _personTargetKey: key,
          _personFieldGroup: ctx.fieldGroup || 'singbac-incorporation-pre',
        },
        nextStepId: ctx.defaultRole ? 'officer-list' : 'officer-role',
        nextStepWidgetData: {
          selectedRole: ctx.defaultRole || undefined,
          role: ctx.defaultRole || undefined,
          entityCd:
            String(ctx.defaultRole || '').toUpperCase() === 'CONTACT'
              ? ''
              : ctx.entityCd || context.formData?.entityCd || '',
          ...(ctx.memberEntityKind ? { memberEntityKind: ctx.memberEntityKind } : {}),
        } as any,
      };
    }

    if (eventName === 'request-address-input') {
      const key = String(payload?.key || payload || '').trim();
      return {
        messages: [
          {
            role: 'user',
            content: key ? `Enter address for **${key}**.` : 'Enter an address.',
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: { _addressTargetKey: key },
        nextStepId: 'address-input',
      };
    }

    if (eventName === 'officer-role-select') {
      const role = typeof payload === 'string' ? payload : payload?.role || payload?.value;
      if (!role) return {};
      return {
        messages: [{ role: 'user', content: `Role: **${role}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-list',
        nextStepWidgetData: {
          selectedRole: role,
          role,
          entityCd:
            String(role || '').toUpperCase() === 'CONTACT' ? '' : context.formData?.entityCd || '',
        } as any,
      };
    }

    if (eventName === 'officer-select' || eventName === 'person-select') {
      return {
        messages: [{ role: 'user', content: 'Person selected.', timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-detail',
        formUpdates: { _selectedOfficer: payload },
      };
    }

    if (eventName === 'officer-field-apply' || eventName === 'value-submit') {
      if (context.currentStepId === 'officer-detail' || context.currentStepId === 'address-input') {
        const aCd = String((context.formData as any)?.customFormAnswerCd || '').trim();
        const fCd = String((context.formData as any)?.customFormCd || '').trim();
        return {
          messages: [
            { role: 'user', content: 'Applied to the document.', timestamp: Date.now(), stepId: 1 },
          ],
          nextStepId: 'confirm',
          // confirm 재진입 시에도 커스텀폼 팔레트 유지 (선택했던 응답 기준).
          ...(aCd
            ? { nextStepWidgetData: { customFormAnswerCd: aCd, customFormCd: fCd } as any }
            : {}),
        };
      }
    }

    return {};
  },
};
